/**
 * Sound utility functions for game audio effects
 */

// Sound file imports
import dropCoinSound from '../assets/media/drop-coin.mp3';
import selectColumnButtonSound from '../assets/media/select-column-button.mp3';
import winGame01Sound from '../assets/media/win-game-01.mp3';
import winGame02Sound from '../assets/media/win-game-02.mp3';
import gameOver01Sound from '../assets/media/game-over-01.mp3';
import gameOver02Sound from '../assets/media/game-over-02.mp3';
import myTurn01Sound from '../assets/media/my-turn-01.mp3';
import myTurn02Sound from '../assets/media/my-turn-02.mp3';
import gameMusicLoop from '../assets/media/game-music-loop-.mp3';

/**
 * Enum for available sound effects
 */
export enum SoundEffect {
  DropCoin = 'drop-coin',
  SelectColumnButton = 'select-column-button',
  WinGame01 = 'win-game-01',
  WinGame02 = 'win-game-02',
  GameOver01 = 'game-over-01',
  GameOver02 = 'game-over-02',
  MyTurn01 = 'my-turn-01',
  MyTurn02 = 'my-turn-02',
}

/**
 * Map of sound effects to their file paths
 */
const SOUND_FILES: Record<SoundEffect, string> = {
  [SoundEffect.DropCoin]: dropCoinSound,
  [SoundEffect.SelectColumnButton]: selectColumnButtonSound,
  [SoundEffect.WinGame01]: winGame01Sound,
  [SoundEffect.WinGame02]: winGame02Sound,
  [SoundEffect.GameOver01]: gameOver01Sound,
  [SoundEffect.GameOver02]: gameOver02Sound,
  [SoundEffect.MyTurn01]: myTurn01Sound,
  [SoundEffect.MyTurn02]: myTurn02Sound,
};

/**
 * Play a sound effect
 * @param effect - The sound effect to play
 * @param volume - Volume level (0-1), defaults to 0.7
 */
export const playSound = (effect: SoundEffect, volume: number = 0.7): void => {
  try {
    const audio = new Audio(SOUND_FILES[effect]);
    audio.volume = Math.max(0, Math.min(1, volume)); // Clamp volume between 0 and 1
    audio.play().catch((error) => {
      console.warn(`Failed to play sound effect ${effect}:`, error);
    });
  } catch (error) {
    console.warn(`Error creating audio for sound effect ${effect}:`, error);
  }
};

/**
 * Preload sound effects for better performance
 * @param effects - Array of sound effects to preload
 */
export const preloadSounds = (effects: SoundEffect[]): void => {
  effects.forEach((effect) => {
    try {
      const audio = new Audio(SOUND_FILES[effect]);
      audio.preload = 'auto';
      audio.load();
    } catch (error) {
      console.warn(`Failed to preload sound effect ${effect}:`, error);
    }
  });
};

/**
 * Play a random win game sound effect
 * @param volume - Volume level (0-1), defaults to 0.7
 */
export const playRandomWinSound = (volume: number = 0.7): void => {
  const winSounds = [SoundEffect.WinGame01, SoundEffect.WinGame02];
  const randomSound = winSounds[Math.floor(Math.random() * winSounds.length)];
  playSound(randomSound, volume);
};

/**
 * Play a random game over sound effect
 * @param volume - Volume level (0-1), defaults to 0.7
 */
export const playRandomGameOverSound = (volume: number = 0.7): void => {
  const gameOverSounds = [SoundEffect.GameOver01, SoundEffect.GameOver02];
  const randomSound = gameOverSounds[Math.floor(Math.random() * gameOverSounds.length)];
  playSound(randomSound, volume);
};

/**
 * Play a random my turn sound effect
 * @param volume - Volume level (0-1), defaults to 0.7
 */
export const playRandomMyTurnSound = (volume: number = 0.7): void => {
  const myTurnSounds = [SoundEffect.MyTurn01, SoundEffect.MyTurn02];
  const randomSound = myTurnSounds[Math.floor(Math.random() * myTurnSounds.length)];
  playSound(randomSound, volume);
};

/**
 * Background music manager class
 */
class BackgroundMusicManager {
  private audio: HTMLAudioElement | null = null;
  private isPlaying: boolean = false;
  private volume: number = 0.3;
  private hasUserInteracted: boolean = false;

  constructor() {
    this.initializeAudio();
    this.setupUserInteractionDetection();
  }

  private setupUserInteractionDetection(): void {
    const markUserInteraction = () => {
      this.hasUserInteracted = true;
      console.log('User interaction detected for audio');
      document.removeEventListener('click', markUserInteraction);
      document.removeEventListener('keydown', markUserInteraction);
      document.removeEventListener('touchstart', markUserInteraction);
    };

    document.addEventListener('click', markUserInteraction);
    document.addEventListener('keydown', markUserInteraction);
    document.addEventListener('touchstart', markUserInteraction);
  }

  private initializeAudio(): void {
    try {
      this.audio = new Audio(gameMusicLoop);
      this.audio.loop = true;
      this.audio.volume = this.volume;
      this.audio.preload = 'auto';
      
      // Add event listeners to track actual audio state
      this.audio.addEventListener('play', () => {
        this.isPlaying = true;
        console.log('Background music started playing');
      });
      
      this.audio.addEventListener('pause', () => {
        this.isPlaying = false;
        console.log('Background music paused');
      });
      
      this.audio.addEventListener('ended', () => {
        this.isPlaying = false;
        console.log('Background music ended');
      });
      
      this.audio.addEventListener('error', (error) => {
        console.error('Background music error:', error);
        this.isPlaying = false;
      });

      this.audio.addEventListener('loadstart', () => {
        console.log('Background music loading started');
      });

      this.audio.addEventListener('canplay', () => {
        console.log('Background music can play');
      });
      
    } catch (error) {
      console.warn('Failed to initialize background music:', error);
    }
  }

  public play(): void {
    if (!this.audio) {
      console.log('Initializing audio...');
      this.initializeAudio();
    }
    
    if (this.audio && !this.isPlaying) {
      console.log('Attempting to play background music...');
      console.log('Has user interacted:', this.hasUserInteracted);
      console.log('Audio file src:', this.audio.src);
      
      this.audio.play().then(() => {
        console.log('Background music play promise resolved');
      }).catch((error) => {
        console.warn('Failed to play background music:', error);
        // Try to handle autoplay restrictions
        if (error.name === 'NotAllowedError') {
          console.log('Autoplay was prevented. User interaction required.');
          if (!this.hasUserInteracted) {
            console.log('No user interaction detected yet. Waiting for user interaction...');
          }
        }
      });
    }
  }

  public pause(): void {
    if (this.audio && this.isPlaying) {
      console.log('Pausing background music...');
      this.audio.pause();
    }
  }

  public toggle(): boolean {
    console.log('Toggling background music. Current state:', this.isPlaying);
    
    // Mark user interaction when toggling
    this.hasUserInteracted = true;
    
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
    return this.isPlaying;
  }

  public setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.audio) {
      this.audio.volume = this.volume;
      console.log('Volume set to:', this.volume);
    }
  }

  public getIsPlaying(): boolean {
    return this.isPlaying;
  }
}

// Create a singleton instance of the background music manager
export const backgroundMusicManager = new BackgroundMusicManager();